import { DEFAULT_THEME, EmailDocument, HOTELSPOT_LOGO_URL } from '../model/types';

/**
 * Szablony startowe.
 *
 * Wszystkie trzymają ten sam, celowo surowy układ: logo, nagłówek, cienka linia, treść,
 * niewielki przycisk i stopka. Bez kolorowych pudełek i kart, bo wiadomość z panelu ma
 * czytać się jak list od zespołu, a nie jak newsletter. Po wybraniu szablon wpada do
 * kreatora jako zwykłe bloki i zmienia się w nim wszystko.
 */

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  build: () => EmailDocument;
}

type Blocks = EmailDocument['blocks'];

const doc = (name: string, subject: string, preheader: string, blocks: Blocks): EmailDocument => ({
  name,
  subject,
  preheader,
  theme: { ...DEFAULT_THEME },
  blocks,
});

const logo = (id: string): Blocks[number] => ({
  id,
  kind: 'logo',
  src: HOTELSPOT_LOGO_URL,
  alt: 'hotelspot',
  height: 26,
  align: 'left',
});

const line = (id: string): Blocks[number] => ({ id, kind: 'divider' });

const signature = (id: string): Blocks[number] => ({
  id,
  kind: 'text',
  text: 'Zespół hotelspot',
  align: 'left',
  small: true,
});

const footer = (id: string): Blocks[number] => ({
  id,
  kind: 'footer',
  text: 'Wiadomość od hotelspot do administratorów obiektu {{hotelName}}.',
  unsubscribeUrl: '{{unsubscribeUrl}}',
  unsubscribeLabel: 'Nie chcę takich wiadomości',
});

export const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'blank',
    name: 'Pusty',
    description: 'Logo, nagłówek, linia i akapit. Reszta do dołożenia.',
    build: () => doc('Nowa wiadomość', 'Temat wiadomości', 'Zajawka widoczna obok tematu', [
      logo('b-0'),
      { id: 'b-1', kind: 'heading', text: 'Nagłówek wiadomości', level: 1, align: 'left' },
      line('b-2'),
      { id: 'b-3', kind: 'text', text: 'Cześć {{firstName}},\n\ntu wpisz treść.', align: 'left', small: false },
      signature('b-4'),
      line('b-5'),
      footer('b-6'),
    ]),
  },
  {
    id: 'welcome',
    name: 'Powitanie',
    description: 'Pierwsza wiadomość po założeniu konta, z trzema krokami na start.',
    build: () => doc(
      'Powitanie w hotelspot',
      'Witamy w hotelspot',
      'Trzy kroki do uruchomienia aplikacji dla gości',
      [
        logo('w-0'),
        { id: 'w-1', kind: 'heading', text: 'Witaj w hotelspot, {{firstName}}', level: 1, align: 'left' },
        line('w-2'),
        {
          id: 'w-3',
          kind: 'text',
          text: 'Konto obiektu {{hotelName}} jest gotowe. Zanim goście zobaczą aplikację, '
            + 'warto zająć się trzema rzeczami.',
          align: 'left',
          small: false,
        },
        {
          id: 'w-4',
          kind: 'list',
          items: [
            'Uzupełnij informacje o hotelu i zdjęcia',
            'Dodaj pierwsze produkty do room service',
            'Wygeneruj kody QR do pokoi',
          ],
        },
        { id: 'w-5', kind: 'button', label: 'Otwórz panel', href: '{{panelUrl}}', align: 'left', fullWidth: false },
        signature('w-6'),
        line('w-7'),
        footer('w-8'),
      ],
    ),
  },
  {
    id: 'feature',
    name: 'Nowa funkcja',
    description: 'Zapowiedź zmiany w panelu, ze zrzutem ekranu i odnośnikiem.',
    build: () => doc(
      'Zapowiedź funkcji',
      'Nowość w panelu',
      'Zobacz, co się zmieniło i co to daje Twojemu obiektowi',
      [
        logo('f-0'),
        { id: 'f-1', kind: 'heading', text: 'Nowość w panelu', level: 1, align: 'left' },
        line('f-2'),
        {
          id: 'f-3',
          kind: 'text',
          text: 'Cześć {{firstName}}, wprowadziliśmy zmianę, która skraca codzienną pracę recepcji.',
          align: 'left',
          small: false,
        },
        { id: 'f-4', kind: 'image', src: '', alt: 'Zrzut ekranu nowej funkcji', href: '', radius: 8 },
        {
          id: 'f-5',
          kind: 'list',
          items: ['Co się zmieniło', 'Co to daje na co dzień', 'Gdzie tego szukać w panelu'],
        },
        { id: 'f-6', kind: 'button', label: 'Zobacz w panelu', href: '{{panelUrl}}', align: 'left', fullWidth: false },
        signature('f-7'),
        line('f-8'),
        footer('f-9'),
      ],
    ),
  },
  {
    id: 'reminder',
    name: 'Przypomnienie',
    description: 'Krótka wiadomość o kończącym się okresie próbnym lub zaległej płatności.',
    build: () => doc(
      'Przypomnienie o koncie',
      'Twój okres próbny dobiega końca',
      'Zachowaj dostęp do aplikacji dla gości',
      [
        logo('r-0'),
        { id: 'r-1', kind: 'heading', text: 'Okres próbny dobiega końca', level: 1, align: 'left' },
        line('r-2'),
        {
          id: 'r-3',
          kind: 'text',
          text: 'Cześć {{firstName}}, konto obiektu {{hotelName}} działa w wersji próbnej. '
            + 'Po jej zakończeniu aplikacja przestanie być widoczna dla gości.',
          align: 'left',
          small: false,
        },
        { id: 'r-4', kind: 'button', label: 'Wybierz plan', href: '{{panelUrl}}', align: 'left', fullWidth: false },
        {
          id: 'r-5',
          kind: 'text',
          text: 'Potrzebujesz więcej czasu na decyzję? Odpisz na tę wiadomość.',
          align: 'left',
          small: true,
        },
        signature('r-6'),
        line('r-7'),
        footer('r-8'),
      ],
    ),
  },
];
